// src/components/FilterTree.js
import React, { useState, useEffect } from 'react';
import { fetchData } from '../services/api';
import { FILTER_CONFIG } from '../components/config';
import '../styles/FilterTree.css';

const FilterTree = ({ onFilterChange, debugMode }) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [filterValues, setFilterValues] = useState({});
  const [availableFilters, setAvailableFilters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableOptions, setAvailableOptions] = useState({});
  const [filterPath, setFilterPath] = useState([]);

  // Initial categories from filter config
  const categories = Object.keys(FILTER_CONFIG).map(key => ({
    value: key,
    label: key.replace(/^\//, '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }));

  useEffect(() => {
    // Reset when changing categories
    if (selectedCategory) {
      const config = FILTER_CONFIG[selectedCategory];
      if (config) {
        // Get the available filters for this category
        const filters = Object.keys(config.validFilters).filter(key => config.validFilters[key].display !== false);
        setAvailableFilters(filters);
        
        // Reset filter values
        setFilterValues({});
        
        // Update filter path - first level is the category
        const newFilterPath = [selectedCategory];
        setFilterPath(newFilterPath);
        
        // Notify parent of the filter change
        onFilterChange(newFilterPath);
        
        // Load options for ALL available filters, not just the first one
        filters.forEach(filter => {
          loadFilterOptions(selectedCategory, filter);
        });
      }
    }
  }, [selectedCategory]);

  const loadFilterOptions = async (category, filterName) => {
    if (!category || !filterName) return;
    
    try {
      setLoading(true);
      const config = FILTER_CONFIG[category];

      // Check if there are predefined options in the config
      const filterConfig = config?.validFilters[filterName];
      const predefinedOptions = filterConfig?.options;
      
        // Check if there are predefined options in the config
     
      if (predefinedOptions) {
        setAvailableOptions(prev => ({
          ...prev,
          [filterName]: predefinedOptions.map(value => ({ value, label: value }))
        }));
        setLoading(false);
        return;
      }
      
      // In debug mode, use hardcoded options
      if (debugMode) {
        // Simulate different options based on filter type
        let mockOptions;
        
        switch(filterName) {
          case 'year':
            mockOptions = ['2020', '2022', '2024'].map(value => ({ value, label: value }));
            break;
          case 'party':
            mockOptions = ['DEM', 'REP', 'LIB', 'GRN', 'IND'].map(value => ({ value, label: value }));
            break;
          case 'contest':
            mockOptions = ['U.S. President', 'U.S. Representative', 'State Senate', 'State House'].map(value => ({ value, label: value }));
            break;
          case 'district':
            mockOptions = ['District 1', 'District 2', 'District 3', 'District 4', 'District 5'].map(value => ({ value, label: value }));
            break;
          case 'name':
            mockOptions = ['Harris/Walz', 'Trump/Vance', 'Begich, Nick', 'Peltola, Mary S.'].map(value => ({ value, label: value }));
            break;
          default:
            mockOptions = [];
        }
        
        setAvailableOptions(prev => ({
          ...prev,
          [filterName]: mockOptions
        }));
      } else {
        // Use the real API
        // For real implementation, we need to build the appropriate query
        // based on previously selected filters
        const previousFilters = {};
        Object.keys(filterValues).forEach(key => {
          if (filterValues[key]) {
            previousFilters[config.validFilters[key]] = filterValues[key];
          }
        });
        
        // Fetch data from API
        try {
          const data = await fetchData(config.endpoint, previousFilters);
          
          // Extract unique values for this filter
          const filterKey = config.validFilters[filterName];
          if (data && Array.isArray(data)) {
            const uniqueValues = [...new Set(data.map(item => item[filterKey] || 'N/A'))];
            
            setAvailableOptions(prev => ({
              ...prev,
              [filterName]: uniqueValues.map(value => ({ value, label: value }))
            }));
          }
        } catch (error) {
          console.error('API error:', error);
          // In case of API error, set empty options
          setAvailableOptions(prev => ({
            ...prev,
            [filterName]: []
          }));
        }
      }
    } catch (error) {
      console.error('Error loading filter options:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setSelectedCategory(category);
    
    // Reset all filters
    setFilterValues({});
    setAvailableOptions({});
  };

  const handleFilterChange = (filterName, value) => {
    const newFilterValues = {
      ...filterValues,
      [filterName]: value
    };
    
    setFilterValues(newFilterValues);
    
    // Update filter path
    const newFilterPath = [selectedCategory];
    
    // Add all selected filter values to the path
    Object.keys(newFilterValues).forEach(key => {
      if (newFilterValues[key]) {
        newFilterPath.push(newFilterValues[key]);
      }
    });
    
    setFilterPath(newFilterPath);
    
    // Notify parent of the filter change
    onFilterChange(newFilterPath);
    
    // When a filter changes, load options for next filters
    if (value && availableFilters.indexOf(filterName) < availableFilters.length - 1) {
      const nextFilter = availableFilters[availableFilters.indexOf(filterName) + 1];
      loadFilterOptions(selectedCategory, nextFilter);
    }
  };

  return (
    <div className="filter-tree">
      <div className="filter-level">
        <label>Select Election Category:</label>
        <select 
          value={selectedCategory} 
          onChange={handleCategoryChange}
          disabled={loading}
        >
          <option value="">Select a category</option>
          {categories.map(category => (
            <option key={category.value} value={category.value}>
              {category.label}
            </option>
          ))}
        </select>
      </div>
      
      {selectedCategory && availableFilters.map(filterName => {
        const filterConfig = FILTER_CONFIG[selectedCategory]?.validFilters[filterName];
        // Only show filters that should be displayed (display isn't false)
        if (filterConfig?.display === false) return null;
        
        return (
          <div className="filter-level" key={filterName}>
            <label>{filterConfig?.displayName || filterName.charAt(0).toUpperCase() + filterName.slice(1)}:</label>
            <select
              value={filterValues[filterName] || ''}
              onChange={(e) => handleFilterChange(filterName, e.target.value)}
              disabled={loading || !availableOptions[filterName]}
            >
              <option value="">Select {filterConfig?.displayName || filterName}</option>
              {availableOptions[filterName]?.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        );
      })}
      
      {loading && <div className="loading-indicator">Loading options...</div>}
      
      {debugMode && (
        <div className="debug-info">
          <h4>Debug Info:</h4>
          <p>Selected Category: {selectedCategory || 'None'}</p>
          <p>Filter Path: {filterPath.join(' > ')}</p>
          <p>Filter Values: {JSON.stringify(filterValues)}</p>
        </div>
      )}
    </div>
  );
};

export default FilterTree;